<template>
  <div id="users" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between align-center">
      <div>
        <h1 class="text-xl text_color font-semibold">Personal Mailboxes</h1>
        <h2 class="mt-2 secondary_color">
          Connect your personal email to send, receive and manage your email
          conversations.
        </h2>
      </div>
      <CustomDropdown
        maxHeight="200px"
        gap="10px"
        position="end"
        :relative-to-viewport="true"
      >
        <template #trigger>
          <a-button class="primary_medium">Connect Mailbox</a-button>
        </template>
        <template #default>
          <div
            v-for="vendor in emailVendorOptions"
            :key="vendor"
            class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
            @click="connectMailbox(vendor, true)"
          >
            <ThemeImage
              :icon="emailVendorIcons[vendor]"
              alt="email_vendor_icons"
              class="size-[16px]"
            />
            <span class="proxima_nova text-base text_color">
              <template v-if="vendor === 'OUTLOOK' || vendor === 'GMAIL'">
                {{ toTitleCase(vendor) }} Account
              </template>
              <template v-else-if="vendor === 'SMTP'">Others</template>
              <template v-else>
                {{ toTitleCase(vendor) }}
              </template>
            </span>
          </div>
        </template>
      </CustomDropdown>
    </div>
    <div class="flex flex-wrap items-center justify-start gap-[24px]">
      <div
        class="flex items-center space-x-2"
        v-for="tracking in trackingOptions"
        :key="tracking.type"
      >
        <a-switch
          v-model:checked="tracking.enabled"
          size="small"
          @change="() => handleTrackingToggle(tracking.type, tracking.enabled)"
        />
        <span class="text-[13px] secondary_color text-base ml-2 text_color">
          Enable {{ tracking.label }} tracking
        </span>
      </div>
      <div class="flex items-center space-x-2">
        <a-switch
          v-model:checked="meetingLeadAutocreateEnabled"
          size="small"
          @change="handleMeetingLeadAutocreateToggle"
        />
        <span class="text-[13px] secondary_color text-base ml-2 text_color">
          Auto-create Leads from Meeting Agent attendees
        </span>
      </div>
    </div>

    <CustomTable
      v-if="primaryMailboxes.length > 0"
      :headers="mailboxHeaders"
      :data="primaryMailboxes"
      :custom-column-render="customColumnRenderWithProps"
      :show-actions-column="true"
      :actions="primaryActions"
      prefixImageBorderRadius="0%"
      :key="tableKey"
      :custom-row="customRow"
    />
  </div>
  <smtp-config
    v-if="isOtherMailboxDrawerVisible"
    v-model:drawerVisible="isOtherMailboxDrawerVisible"
    @update:isOtherMailboxDrawerVisible="
      (val) => (isOtherMailboxDrawerVisible = val) && (editingMailboxId = null)
    "
    :email-account-id="editingMailboxId"
    @smtpMailBoxAdded="() => fetchMailboxes()"
    :is-primary-mailbox="true"
  />
</template>

<script setup>
import { ref, onMounted } from "vue";
import DashboardService from "@/src/api/DashboardService.js";
import IntegrationService from "@/src/api/IntegrationService.js";
import { toTitleCase } from "@/src/utils/utils.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import { emailVendorIcons } from "@/src/constants/settingConstants.js";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";

import { useRouter } from "vue-router";
import SmtpConfig from "@/src/components/SmtpConfig.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useSmtpReconnect } from "@/src/composables/useMailbox.js";

const router = useRouter();
const openTrackingEnabled = ref(false);
const clickTrackingEnabled = ref(false);
const meetingLeadAutocreateEnabled = ref(false);
const primaryMailboxes = ref([]);
const tableKey = ref(0);

const trackingOptions = ref([
  { type: "open", label: "open", enabled: openTrackingEnabled },
  { type: "click", label: "click", enabled: clickTrackingEnabled },
]);

const mailboxHeaders = ref([
  { label: "Sender Account", key: "email", width: 12 },
]);

const editingMailboxId = ref(null);
const isOtherMailboxDrawerVisible = ref(false);

const { customColumnRenderWithProps } = useSmtpReconnect(
  editingMailboxId,
  isOtherMailboxDrawerVisible
);

const primaryActions = [
  {
    label: "Edit Mailbox",
    handler: (row) => manageSignature(row),
    icon: imageNames.SIGNATURE_GREY,
  },
  {
    label: "Mark as shared",
    handler: (row) => markAsShared(row),
    icon: imageNames.STAR_GREY,
  },
  {
    label: "Mark as primary",
    handler: (row) => markAsPrimary(row),
    icon: imageNames.STAR_GREY,
    condition: (row) => !row.email.is_primary,
  },
  {
    label: "Disconnect",
    handler: (row) => disconnectMailbox(row),
    icon: imageNames.DISCONNECT_GREY,
    confirmRequired: true,
    confirmTitle: "Disconnect Mailbox",
    confirmMessage: "Are you sure you want to disconnect this mailbox?",
    condition: (row) => row.email.status === "CONNECTED",
  },
  {
    label: "Delete",
    handler: (row) => deleteMailbox(row),
    icon: imageNames.DELETE_GREY,
    confirmRequired: true,
    confirmTitle: "Delete Mailbox",
    confirmMessage: "Are you sure you want to delete this mailbox?",
    condition: (row) => row.email.status === "DISCONNECTED",
  },
];

const manageSignature = (row) => {
  router.push({ name: "mailbox-signature", params: { id: row.email.id } });
};

const markAsShared = async (row) => {
  try {
    const response = await IntegrationService.setEmailAccountSharing(row.email.id, true);
    if (response.data.success) {
      await fetchMailboxes();
      message.success("Mailbox successfully moved to the shared mailbox");
    } else {
      message.error(response.data.error || "Failed to mark mailbox as shared");
    }
  } catch (error) {
    console.error("Error marking mailbox as shared:", error);
    message.error("An error occurred while marking mailbox as shared");
  }
};

const markAsPrimary = async (row) => {
  if (row.email.status == "DISCONNECTED") { 
    message.error("Cannot mark a disconnected mailbox as primary");
    return;
  }

  const response = await DashboardService.markAsPrimary(row.email.id);
  if (response.success) {
    message.success("Mailbox marked as primary successfully");
    await fetchMailboxes();
  } else {
    message.error("Failed to mark mailbox as primary");
  }
};

const getPrimaryMailboxesTableData = (mailboxes) => {
  return mailboxes.map((mailbox) => {
    return {
      email: {
        text: mailbox.email,
        prefixImage: emailVendorIcons[mailbox.vendor],
        status: mailbox.status,
        is_primary: mailbox.is_primary,
        vendor: mailbox.vendor,
        id: mailbox.id,
      },
    };
  });
};

const emailVendorOptions = ref(["OUTLOOK", "GMAIL", "SMTP"]);

const fetchMailboxes = async () => {
  try {
    const response = await DashboardService.fetchPersonalMailboxes();
    if (response.success) {
      primaryMailboxes.value = getPrimaryMailboxesTableData(
        response.email_accounts
      );
      tableKey.value += 1;
    }
  } catch (error) {
    message.error("Error fetching personal mailboxes");
  }
};

const connectMailbox = async (vendor, primary) => {
  try {
    if (vendor === "SMTP") {
      editingMailboxId.value = null;
      isOtherMailboxDrawerVisible.value = true;
      return;
    }
    const response = await DashboardService.connectPersonalMailbox(vendor);
    if (response.success && response.redirect_url) {
      window.open(response.redirect_url);
      await fetchMailboxes();
    }
  } catch (error) {
    message.error("Error connecting mailbox");
  }
};

const disconnectMailbox = async (row) => {
  try {
    const payload = {
      vendor: row.email.vendor,
      primary: true,
      email: row.email.text,
    };
    const response = await DashboardService.disconnectMailbox(payload);
    if (response.success) {
      message.success("Mailbox disconnected successfully");
      await fetchMailboxes();
    } else {
      message.error("Failed to disconnect mailbox");
    }
  } catch (error) {
    message.error("An error occurred while disconnecting the mailbox");
  }
};

const deleteMailbox = async (row) => {
  try {
    const response = await DashboardService.deleteMailbox(row.email.id);
    if (response.success) {
      message.success("Mailbox deleted successfully");
      await fetchMailboxes();
    } else {
      message.error(response.error || "Failed to delete mailbox");
    }
  } catch (error) {
    console.error("Error deleting mailbox:", error);
    message.error("An error occurred while deleting the mailbox");
  }
};

const handleTrackingToggle = async (trackingType, newState) => {
  const trackingMap = {
    open: {
      toggleFunction: DashboardService.setOpenTracking,
      state: openTrackingEnabled,
    },
    click: {
      toggleFunction: DashboardService.setClickTracking,
      state: clickTrackingEnabled,
    },
  };
  try {
    const { toggleFunction, state } = trackingMap[trackingType];
    const response = await toggleFunction(!newState);

    if (response.success) {
      state.value = !response[`${trackingType}_tracking_enabled`];

      message.success(
        `${capitalizeFirstLetter(trackingType)} tracking ${
          state.value ? "enabled" : "disabled"
        }`
      );
    } else {
      state.value = !newState;
      message.error(`Failed to update ${trackingType} tracking setting`);
    }
  } catch (error) {
    console.error(`Error updating ${trackingType} tracking:`, error);

    trackingMap[trackingType].state.value = !newState;
    message.error(
      `An error occurred while updating ${trackingType} tracking setting`
    );
  }
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const handleMeetingLeadAutocreateToggle = async (newState) => {
  try {
    const response = await DashboardService.setMeetingLeadAutocreate(newState);
    if (response.success) {
      meetingLeadAutocreateEnabled.value = response.meeting_lead_autocreate_enabled;
      message.success(
        `Meeting Agent lead auto-creation ${response.meeting_lead_autocreate_enabled ? "enabled" : "disabled"}`,
      );
    } else {
      meetingLeadAutocreateEnabled.value = !newState;
      message.error("Failed to update meeting lead auto-create setting");
    }
  } catch (error) {
    meetingLeadAutocreateEnabled.value = !newState;
    message.error("An error occurred while updating meeting lead auto-create setting");
  }
};

const fetchMeetingLeadAutocreateState = async () => {
  try {
    const response = await DashboardService.fetchMeetingLeadAutocreateState();
    if (response.success) {
      meetingLeadAutocreateEnabled.value = response.meeting_lead_autocreate_enabled;
    }
  } catch (error) {
    message.error("Failed to fetch meeting lead auto-create state");
  }
};

const fetchTrackingState = async (trackingType) => {
  try {
    const trackingMap = {
      open: {
        fetchFunction: DashboardService.fetchOpenTrackingState,
        state: openTrackingEnabled,
      },
      click: {
        fetchFunction: DashboardService.fetchClickTrackingState,
        state: clickTrackingEnabled,
      },
    };

    const { fetchFunction, state } = trackingMap[trackingType];

    const response = await fetchFunction();
    if (response.success) {
      state.value = !response[`${trackingType}_tracking_enabled`];
    }
  } catch (error) {
    console.error(`Error fetching ${trackingType} tracking state:`, error);
    message.error(`Failed to fetch ${trackingType} tracking state`);
  }
};

const customRow = (record) => {
  return {
    onClick: () => {
      router.push({
        name: "mailbox-signature",
        params: { id: record.email.id },
      });
    },
  };
};

onMounted(() => {
  fetchMailboxes();
  fetchTrackingState("open");
  fetchTrackingState("click");
  fetchMeetingLeadAutocreateState();
  const route = useRoute();
  const queryParams = route.query;
  
  if (queryParams?.reconnect_smtp_id) {
    editingMailboxId.value = parseInt(queryParams.reconnect_smtp_id);
    isOtherMailboxDrawerVisible.value = true;
    router.replace({ name: 'settings', hash: '#personal_mailboxes' });
  }
  
  if (queryParams && queryParams.success && queryParams.success === "true") {
    message.success("Mailbox connected successfully");
  } else if (
    queryParams &&
    queryParams.permissions &&
    queryParams.permissions === "false"
  ) {
     message.error("Mailbox couldn't be connected because of insufficient permission.");
  } 
  else if (
    queryParams &&
    queryParams.duplicate_email &&
    queryParams.duplicate_email === "true"
  ) {
    message.error("Email already connected in this workspace by another team member");
  }
  else if (
    queryParams &&
    queryParams.success &&
    queryParams.success === "false"
  ) {
    const errorMessage = queryParams.error 
      ? decodeURIComponent(queryParams.error.replace(/\+/g, ' '))
      : "Failed to connect mailbox";
    message.error(errorMessage);
  }
});
</script>
<style scoped>
.dropdown-mailboxes {
  background: var(--dropdown-bg);
}

.dropdown-item-mailboxes {
  color: var(--white-opacity-80);
}

.dropdown-item-mailboxes:hover {
  background: #2f2f32;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  padding: 6px 12px;
  border-radius: 8px;
}

.dropdown-item:hover {
  background-color: var(--white-opacity-10);
}
</style>
